/**
 * useProject Hook
 * 
 * Manages project state including:
 * - Loading/saving projects
 * - Auto-save functionality
 * - Dirty state tracking
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { 
  getProject, 
  createProject, 
  updateProject, 
  renameProject as apiRenameProject 
} from '../services/projects';
import type { 
  ProjectData, 
  ProjectState, 
  AutoSaveStatus,
  Project 
} from '../types/project';
import { DEFAULT_PROJECT_DATA } from '../types/project';

// Auto-save interval in milliseconds
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds
const AUTO_SAVE_DEBOUNCE = 2000; // 2 seconds after last change

interface UseProjectReturn {
  // State
  projectState: ProjectState;
  projectData: ProjectData;
  
  // Actions
  loadProject: (projectId: string) => Promise<void>;
  createNewProject: (name: string) => Promise<string>;
  saveProject: (data?: ProjectData) => Promise<void>;
  saveProjectAs: (name: string) => Promise<string>;
  renameProject: (newName: string) => Promise<void>;
  updateProjectData: (updates: Partial<ProjectData>) => void;
  markDirty: () => void;
  resetProject: () => void;
}

export const useProject = (): UseProjectReturn => {
  const { getToken } = useAuth();
  
  // Project state
  const [projectState, setProjectState] = useState<ProjectState>({
    currentProjectId: null,
    currentProjectName: 'Untitled Project',
    isDirty: false,
    lastSavedAt: null,
    autoSaveStatus: 'idle',
  });
  
  // Project data
  const [projectData, setProjectData] = useState<ProjectData>(DEFAULT_PROJECT_DATA);
  
  // Refs for auto-save
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastChangeTimeRef = useRef<number>(Date.now());
  const pendingDataRef = useRef<ProjectData | null>(null);
  const saveProjectRef = useRef<(() => Promise<void>) | null>(null);

  // Load a project by ID
  const loadProject = useCallback(async (projectId: string) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      const project = await getProject(token, projectId);
      
      setProjectData(project.data || DEFAULT_PROJECT_DATA);
      setProjectState({
        currentProjectId: project.id,
        currentProjectName: project.name,
        isDirty: false,
        lastSavedAt: new Date(project.updated_at),
        autoSaveStatus: 'saved',
      });
    } catch (error) {
      console.error('Failed to load project:', error);
      throw error;
    }
  }, [getToken]);

  // Create a new project
  const createNewProject = useCallback(async (name: string): Promise<string> => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      const result = await createProject(token, {
        name,
        data: DEFAULT_PROJECT_DATA,
      });
      
      setProjectData(DEFAULT_PROJECT_DATA);
      setProjectState({
        currentProjectId: result.id,
        currentProjectName: name,
        isDirty: false,
        lastSavedAt: new Date(),
        autoSaveStatus: 'saved',
      });
      
      return result.id;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  }, [getToken]);

  // Save current project
  const saveProject = useCallback(async (data?: ProjectData) => {
    const dataToSave = data || pendingDataRef.current || projectData;
    
    if (!projectState.currentProjectId) {
      return;
    }
    
    setProjectState(prev => ({ ...prev, autoSaveStatus: 'saving' }));
    
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      const result = await updateProject(token, projectState.currentProjectId, {
        data: dataToSave,
      });
      pendingDataRef.current = null;
      setProjectState(prev => ({
        ...prev,
        isDirty: false,
        lastSavedAt: new Date(result.updated_at),
        autoSaveStatus: 'saved',
      }));
    } catch (error) {
      setProjectState(prev => ({ ...prev, autoSaveStatus: 'error' }));
      throw error;
    }
  }, [getToken, projectState.currentProjectId, projectData]);

  // Keep saveProject ref up to date
  useEffect(() => {
    saveProjectRef.current = saveProject;
  }, [saveProject]);

  // Save as new project
  const saveProjectAs = useCallback(async (name: string): Promise<string> => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      const result = await createProject(token, {
        name,
        data: projectData,
      });
      
      setProjectState({
        currentProjectId: result.id,
        currentProjectName: name,
        isDirty: false,
        lastSavedAt: new Date(),
        autoSaveStatus: 'saved',
      });
      
      return result.id;
    } catch (error) {
      console.error('Failed to save project as:', error);
      throw error;
    }
  }, [getToken, projectData]);

  // Rename project
  const renameProject = useCallback(async (newName: string) => {
    if (!projectState.currentProjectId) return;
    
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      await apiRenameProject(token, projectState.currentProjectId, newName);
      
      setProjectState(prev => ({
        ...prev,
        currentProjectName: newName,
      }));
    } catch (error) {
      console.error('Failed to rename project:', error);
      throw error;
    }
  }, [getToken, projectState.currentProjectId]);

  // Update project data (triggers auto-save)
  const updateProjectData = useCallback((updates: Partial<ProjectData>) => {
    setProjectData(prev => {
      const newData = { ...prev, ...updates };
      pendingDataRef.current = newData;
      return newData;
    });
    
    lastChangeTimeRef.current = Date.now();
    setProjectState(prev => ({
      ...prev,
      isDirty: true,
      autoSaveStatus: 'idle',
    }));
  }, []);

  // Mark as dirty without updating data
  const markDirty = useCallback(() => {
    lastChangeTimeRef.current = Date.now();
    setProjectState(prev => ({
      ...prev,
      isDirty: true,
      autoSaveStatus: 'idle',
    }));
  }, []);

  // Reset to empty project
  const resetProject = useCallback(() => {
    setProjectData(DEFAULT_PROJECT_DATA);
    setProjectState({
      currentProjectId: null,
      currentProjectName: 'Untitled Project',
      isDirty: false,
      lastSavedAt: null,
      autoSaveStatus: 'idle',
    });
    pendingDataRef.current = null;
  }, []);

  // Auto-save effect - uses ref to avoid effect restart when saveProject changes
  useEffect(() => {
    const checkAutoSave = () => {
      const timeSinceLastChange = Date.now() - lastChangeTimeRef.current;
      
      // Only auto-save if:
      // 1. There's a project ID
      // 2. There are unsaved changes
      // 3. Enough time has passed since last change
      if (
        projectState.currentProjectId &&
        projectState.isDirty &&
        timeSinceLastChange >= AUTO_SAVE_DEBOUNCE
      ) {
        // Use ref to call latest saveProject without causing effect restart
        saveProjectRef.current?.();
      }
    };

    // Set up interval for auto-save checks
    const intervalId = setInterval(checkAutoSave, AUTO_SAVE_INTERVAL);

    // Also set up a debounced save after changes
    if (projectState.isDirty && projectState.currentProjectId) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(checkAutoSave, AUTO_SAVE_DEBOUNCE);
    }

    return () => {
      clearInterval(intervalId);
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [projectState.isDirty, projectState.currentProjectId]); // Removed saveProject from deps

  // Keyboard shortcut for save (Cmd+S / Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (projectState.currentProjectId) {
          saveProject();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [projectState.currentProjectId, saveProject]);

  return {
    projectState,
    projectData,
    loadProject,
    createNewProject,
    saveProject,
    saveProjectAs,
    renameProject,
    updateProjectData,
    markDirty,
    resetProject,
  };
};

export default useProject;
